import React, { useState, useEffect, useRef, useMemo , useCallback} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useToast } from "../../Toast";
import * as XLSX from "xlsx";
import {
  Get_Leaves,
  Approve_Leave,
  Delete_Leave,
  Get_HolidaysList,
  Post_HolidaysList,
  Update_HolidaysList,
  Delete_HolidaysList,
} from "../../../services/oprations/Bussiness_Logic";
import StandardSpinner from '../../../utils/Spinner';
import {
  FaCheck,
  FaTimes,
  FaEdit,
  FaTrash,
  FaPlus,
  FaSearch,
  FaFilter,
  FaTimesCircle,
  FaChevronDown,
  FaChevronUp,
  FaFileExcel,
  FaDownload,
  FaExclamationTriangle,
  FaCalendarAlt,
} from "react-icons/fa";
import { useTheme } from '../../../Context/themeContext';

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const LeaveManagement = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const toast = useToast();
  const dispatch = useDispatch();
  const { leavesData } = useSelector((state) => state.Leave);
  const { holidaysData } = useSelector((state) => state.Holidays);
  const { token } = useSelector((state) => state.auth);

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("leaves");

  // Search and Filter States (Leaves)
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [leaveTypeFilter, setLeaveTypeFilter] = useState("ALL");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [showFilters, setShowFilters] = useState(false);
  const [expandedLeaveId, setExpandedLeaveId] = useState(null);

  // Search and Filter States (Holidays)
  const [holidaySearchTerm, setHolidaySearchTerm] = useState("");
  const [holidayMonthFilter, setHolidayMonthFilter] = useState("ALL");
  const [holidayYearFilter, setHolidayYearFilter] = useState("ALL");

  // Holiday Modal States
  const [isHolidayModalOpen, setIsHolidayModalOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [holidayForm, setHolidayForm] = useState({
    Holidayname: "",
    DateString: "",
  });

  // Excel Import States
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);

  // ─── Generic Confirmation Modal State ─────────────────────────────────
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    confirmText: "Confirm",
    confirmColor: "bg-red-600 hover:bg-red-700",
    onConfirm: null,
  });
  const [confirmProcessing, setConfirmProcessing] = useState(false);

  const openConfirm = ({
    title,
    message,
    onConfirm,
    confirmText = "Confirm",
    confirmColor = "bg-red-600 hover:bg-red-700",
  }) => {
    setConfirmModal({ isOpen: true, title, message, confirmText, confirmColor, onConfirm });
  };

  const closeConfirm = () => {
    if (confirmProcessing) return;
    setConfirmModal((prev) => ({ ...prev, isOpen: false, onConfirm: null }));
  };

  const handleConfirmAction = async () => {
    if (!confirmModal.onConfirm) return;
    setConfirmProcessing(true);
    try {
      await confirmModal.onConfirm();
    } finally {
      setConfirmProcessing(false);
      setConfirmModal((prev) => ({ ...prev, isOpen: false, onConfirm: null }));
    }
  };

  // ─── Theme-aware classes ──────────────────────────────────────────────
  const bgPrimary = isDark
    ? 'bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900'
    : 'bg-gradient-to-br from-gray-100 via-blue-100/20 to-gray-100';

  const bgCard = isDark ? 'bg-blue-900/30' : 'bg-white/80';
  const bgCardHover = isDark ? 'hover:border-blue-500' : 'hover:border-blue-400';
  const bgInput = isDark ? 'bg-slate-700' : 'bg-white';
  const bgModal = isDark ? 'bg-slate-800' : 'bg-white';
  const bgButton = isDark ? 'bg-blue-700 hover:bg-blue-600' : 'bg-blue-500 hover:bg-blue-600';
  const bgCancel = isDark ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-300 hover:bg-gray-400';
  const bgTableHeader = isDark ? 'bg-slate-800' : 'bg-gray-100';

  const borderColor = isDark ? 'border-blue-700' : 'border-gray-300';
  const borderInput = isDark ? 'border-slate-600' : 'border-gray-300';
  const borderFocus = isDark ? 'focus:border-blue-500' : 'focus:border-blue-500';

  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
 // const textSecondary = isDark ? 'text-blue-300' : 'text-gray-600';
 // const textMuted = isDark ? 'text-blue-400' : 'text-gray-500';
 // const textLight = isDark ? 'text-blue-300' : 'text-gray-600';

  const tabActive = isDark
    ? 'text-white border-b-2 border-blue-400 bg-blue-800/50 rounded-t-lg'
    : 'text-blue-800 border-b-2 border-blue-500 bg-blue-100/50 rounded-t-lg';
  const tabInactive = isDark ? 'text-blue-400 hover:text-white' : 'text-gray-600 hover:text-gray-900';


  const fetchData = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      dispatch(Get_Leaves(token)),
      dispatch(Get_HolidaysList(token)),
    ]);
    setLoading(false);
  },[dispatch, token]);
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApproveLeave = async (leaveId) => {
    setLoading(true);
    const result = await dispatch(Approve_Leave({ leaveId, status: "APPROVED" }, token));
    if (result?.data?.success) {
      toast.success("Leave approved successfully");
    } else {
      toast.error(result?.data?.message || "Failed to approve leave");
    }
    setLoading(false);
  };

  const handleRejectLeave = async (leaveId) => {
    setLoading(true);
    const result = await dispatch(Approve_Leave({ leaveId, status: "REJECTED" }, token));
    if (result?.data?.success) {
      toast.success("Leave rejected successfully");
    } else {
      toast.error(result?.data?.message || "Failed to reject leave");
    }
    setLoading(false);
  };

  const handleDeleteLeave = (leaveId) => {
    openConfirm({
      title: "Delete Leave Request",
      message: "Are you sure you want to delete this leave request? This action cannot be undone.",
      confirmText: "Delete",
      confirmColor: "bg-red-600 hover:bg-red-700",
      onConfirm: async () => {
        setLoading(true);
        const result = await dispatch(Delete_Leave(leaveId, token));
        if (result?.data?.success) {
          toast.success("Leave deleted successfully");
          await fetchData();
        } else {
          toast.error(result?.data?.message || "Failed to delete leave");
        }
        setLoading(false);
      },
    });
  };

  // Holiday CRUD Operations
  const handleAddHoliday = () => {
    setEditingHoliday(null);
    setHolidayForm({ Holidayname: "", DateString: "" });
    setIsHolidayModalOpen(true);
  };

  const handleEditHoliday = (holiday) => {
    setEditingHoliday(holiday);
    setHolidayForm({
      Holidayname: holiday.Holidayname,
      DateString: holiday.DateString.split("T")[0],
    });
    setIsHolidayModalOpen(true);
  };

  const handleDeleteHoliday = (id) => {
    openConfirm({
      title: "Delete Holiday",
      message: "Are you sure you want to delete this holiday? This action cannot be undone.",
      confirmText: "Delete",
      confirmColor: "bg-red-600 hover:bg-red-700",
      onConfirm: async () => {
        setLoading(true);
        const result = await dispatch(Delete_HolidaysList(id, token));
        if (result?.data?.success) {
          toast.success("Holiday deleted successfully");
          await fetchData();
        } else {
          toast.error(result?.data?.message || "Failed to delete holiday");
        }
        setLoading(false);
      },
    });
  };

  const handleSubmitHoliday = async (e) => {
    e.preventDefault();
    setLoading(true);

    let result;
    if (editingHoliday) {
      result = await dispatch(Update_HolidaysList(editingHoliday._id, holidayForm, token));
    } else {
      result = await dispatch(Post_HolidaysList(holidayForm, token));
    }

    if (result?.data?.success) {
      toast.success(editingHoliday ? "Holiday updated successfully" : "Holiday added successfully");
      setIsHolidayModalOpen(false);
      await fetchData();
    } else {
      toast.error(result?.data?.message || "Operation failed");
    }

    setLoading(false);
  };

  // ─── Excel Import for Holidays ─────────────────────────────────────────
  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const parseExcelDate = (value) => {
    if (value === null || value === undefined || value === "") return "";

    if (typeof value === "number") {
      const parsed = XLSX.SSF.parse_date_code(value);
      if (parsed) {
        const mm = String(parsed.m).padStart(2, "0");
        const dd = String(parsed.d).padStart(2, "0");
        return `${parsed.y}-${mm}-${dd}`;
      }
    }

    const asDate = new Date(value);
    if (!isNaN(asDate.getTime())) {
      const yyyy = asDate.getFullYear();
      const mm = String(asDate.getMonth() + 1).padStart(2, "0");
      const dd = String(asDate.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    }

    return "";
  };

  const normalizeRow = (row) => {
    const normalized = {};
    Object.keys(row).forEach((key) => {
      normalized[key.trim().toLowerCase()] = row[key];
    });
    return normalized;
  };

  const handleFileImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];

      const headerRow = XLSX.utils.sheet_to_json(sheet, { header: 1 })[0] || [];
      const normalizedHeaders = headerRow
        .map((h) => String(h).trim().toLowerCase())
        .filter((h) => h !== "");

      const expectedHeaders = ["date", "holiday name"];

      if (normalizedHeaders.length !== 2) {
        toast.error(
          `Invalid file. Expected exactly 2 columns ("Date" and "Holiday Name"), found ${normalizedHeaders.length}.`
        );
        e.target.value = "";
        return;
      }

      const hasExactMatch =
        normalizedHeaders.includes("date") &&
        normalizedHeaders.includes("holiday name") &&
        expectedHeaders.every((h) => normalizedHeaders.includes(h));

      if (!hasExactMatch) {
        toast.error(
          `Invalid column headers. File must have exactly "Date" and "Holiday Name" columns. Found: ${headerRow.join(", ")}`
        );
        e.target.value = "";
        return;
      }

      const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });

      if (!rows.length) {
        toast.error("The Excel file has headers but no data rows.");
        e.target.value = "";
        return;
      }

      const parsedHolidays = rows
        .map((rawRow) => {
          const row = normalizeRow(rawRow);
          const name = row["holiday name"] ?? "";
          const dateStr = parseExcelDate(row["date"]);
          return {
            Holidayname: String(name).trim(),
            DateString: dateStr,
          };
        })
        .filter((h) => h.Holidayname && h.DateString);

      if (!parsedHolidays.length) {
        toast.error(
          "No valid rows found. Every row must have both a Date and a Holiday Name."
        );
        e.target.value = "";
        return;
      }

      const invalidRowCount = rows.length - parsedHolidays.length;

      openConfirm({
        title: "Import Holidays",
        message: `Are you sure you want to import ${parsedHolidays.length} holiday${
          parsedHolidays.length > 1 ? "s" : ""
        } from "${file.name}"?${
          invalidRowCount > 0
            ? ` (${invalidRowCount} row${invalidRowCount > 1 ? "s" : ""} skipped due to missing data)`
            : ""
        }`,
        confirmText: "Import",
        confirmColor: "bg-green-600 hover:bg-green-700",
        onConfirm: () => processImport(parsedHolidays),
      });
    } catch (error) {
      console.error("Excel import error:", error);
      toast.error("Could not read the Excel file. Please check the format and try again.");
    } finally {
      e.target.value = "";
    }
  };

  const processImport = async (parsedHolidays) => {
    setImporting(true);
    let successCount = 0;
    let failCount = 0;

    for (const holiday of parsedHolidays) {
      try {
        const result = await dispatch(Post_HolidaysList(holiday, token));
        if (result?.data?.success) {
          successCount++;
        } else {
          failCount++;
        }
      } catch (err) {
        failCount++;
      }
    }

    if (successCount > 0) {
      toast.success(
        `${successCount} holiday(s) imported successfully${
          failCount ? `, ${failCount} failed` : ""
        }`
      );
      await fetchData();
    } else {
      toast.error("Failed to import holidays. Please check the file and try again.");
    }
    setImporting(false);
  };

  const handleDownloadSample = () => {
    const sampleData = [
      { Date: "2026-01-01", "Holiday Name": "New Year's Day" },
      { Date: "2026-01-26", "Holiday Name": "Republic Day" },
      { Date: "2026-08-15", "Holiday Name": "Independence Day" },
      { Date: "2026-10-02", "Holiday Name": "Gandhi Jayanti" },
      { Date: "2026-12-25", "Holiday Name": "Christmas" },
    ];
    const ws = XLSX.utils.json_to_sheet(sampleData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Holidays");
    XLSX.writeFile(wb, "holidays_sample_template.xlsx");
  };

  // Filter and Search Logic for Leaves
  const getFilteredLeaves = () => {
    if (!leavesData) return [];

    let filtered = [...leavesData];

    if (searchTerm) {
      filtered = filtered.filter(leave =>
        leave.userId?.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        leave.leaveType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        leave.reason?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        leave.userId?.user_department?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== "ALL") {
      filtered = filtered.filter(leave => leave.status === statusFilter);
    }

    if (leaveTypeFilter !== "ALL") {
      filtered = filtered.filter(leave => leave.leaveType === leaveTypeFilter);
    }

    if (dateRange.from) {
      filtered = filtered.filter(leave => new Date(leave.fromDate) >= new Date(dateRange.from));
    }
    if (dateRange.to) {
      filtered = filtered.filter(leave => new Date(leave.toDate) <= new Date(dateRange.to));
    }

    filtered.sort((a, b) => new Date(b.appliedAt) - new Date(a.appliedAt));

    return filtered;
  };

  const filteredLeaves = getFilteredLeaves();
  const uniqueLeaveTypes = [...new Set(leavesData?.map(leave => leave.leaveType) || [])];

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("ALL");
    setLeaveTypeFilter("ALL");
    setDateRange({ from: "", to: "" });
  };

  // ─── Filter and Search Logic for Holidays ──────────────────────────────
  const holidayYears = useMemo(() => {
    if (!holidaysData?.length) return [];
    const years = new Set(
      holidaysData
        .map((h) => {
          const d = new Date(h.DateString);
          return isNaN(d.getTime()) ? null : d.getFullYear();
        })
        .filter(Boolean)
    );
    return [...years].sort((a, b) => b - a);
  }, [holidaysData]);

  const filteredHolidays = useMemo(() => {
    if (!holidaysData) return [];
    let filtered = [...holidaysData];

    if (holidaySearchTerm) {
      filtered = filtered.filter((h) =>
        h.Holidayname?.toLowerCase().includes(holidaySearchTerm.toLowerCase())
      );
    }

    if (holidayMonthFilter !== "ALL") {
      filtered = filtered.filter((h) => {
        const d = new Date(h.DateString);
        return !isNaN(d.getTime()) && d.getMonth() === Number(holidayMonthFilter);
      });
    }

    if (holidayYearFilter !== "ALL") {
      filtered = filtered.filter((h) => {
        const d = new Date(h.DateString);
        return !isNaN(d.getTime()) && d.getFullYear() === Number(holidayYearFilter);
      });
    }

    filtered.sort((a, b) => new Date(a.DateString) - new Date(b.DateString));

    return filtered;
  }, [holidaysData, holidaySearchTerm, holidayMonthFilter, holidayYearFilter]);

  const clearHolidayFilters = () => {
    setHolidaySearchTerm("");
    setHolidayMonthFilter("ALL");
    setHolidayYearFilter("ALL");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      PENDING: "bg-yellow-200/30 text-yellow-500",
      APPROVED: "bg-green-200/40 text-green-500",
      REJECTED: "bg-red-200/50 text-red-400",
    };
    return (
      <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${styles[status] || styles.PENDING}`}>
        {status}
      </span>
    );
  };

  if (loading && !leavesData) {
    return <StandardSpinner />;
  }

  return (
    <div className={`min-h-screen ${bgPrimary} transition-colors duration-300`}>
      <div className="container px-3 py-4 mx-auto sm:px-4 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className={`mb-1 text-2xl font-bold ${textPrimary} sm:text-3xl md:text-4xl sm:mb-2`}>
            Leave Management
          </h1>
          <p className={`text-sm ${isDark ? 'text-blue-300' : 'text-gray-600'} sm:text-base`}>
            Manage employee leaves and company holidays
          </p>
        </div>

        {/* Tabs - Mobile Optimized */}
        <div className={`flex flex-col gap-2 mb-6 border-b ${isDark ? 'border-blue-800' : 'border-gray-200'} sm:flex-row sm:gap-4`}>
          <button
            onClick={() => setActiveTab("leaves")}
            className={`px-4 sm:px-6 py-2 sm:py-3 font-semibold transition-all duration-300 text-sm sm:text-base ${
              activeTab === "leaves" ? tabActive : tabInactive
            }`}
          >
            Leave Requests ({leavesData?.filter(l => l.status === "PENDING").length || 0})
          </button>
          <button
            onClick={() => setActiveTab("holidays")}
            className={`px-4 sm:px-6 py-2 sm:py-3 font-semibold transition-all duration-300 text-sm sm:text-base ${
              activeTab === "holidays" ? tabActive : tabInactive
            }`}
          >
            Holidays ({holidaysData?.length || 0})
          </button>
        </div>

        {/* Leaves Section */}
        {activeTab === "leaves" && (
          <div>
            {/* Search and Filter Bar - Mobile Responsive */}
            <div className={`p-3 mb-6 sm:p-4 ${bgCard} backdrop-blur-sm rounded-xl border ${borderColor} transition-colors duration-300`}>
              <div className="flex flex-col flex-wrap gap-3 sm:flex-row sm:gap-4">
                {/* Search Input */}
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <FaSearch className={`absolute text-sm transform -translate-y-1/2 left-3 top-1/2 ${isDark ? 'text-blue-400' : 'text-gray-500'} sm:text-base`} />
                    <input
                      type="text"
                      placeholder="Search by employee name, leave type..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className={`w-full py-2 pr-3 text-sm border rounded-lg pl-9 sm:pl-10 sm:pr-4 sm:text-base ${textPrimary} ${bgInput} ${borderInput} focus:outline-none ${borderFocus} transition-colors duration-300`}
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center gap-1 px-3 py-2 text-sm text-white transition-all duration-300 rounded-lg sm:gap-2 sm:px-4 sm:text-base ${bgButton}`}
                  >
                    <FaFilter size={14} className="sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Filters</span>
                  </button>

                  {(searchTerm || statusFilter !== "ALL" || leaveTypeFilter !== "ALL" || dateRange.from || dateRange.to) && (
                    <button
                      onClick={clearFilters}
                      className="flex items-center gap-1 px-3 py-2 text-sm text-white transition-all duration-300 bg-red-700 rounded-lg sm:gap-2 sm:px-4 sm:text-base hover:bg-red-600"
                    >
                      <FaTimesCircle size={14} className="sm:w-4 sm:h-4" />
                      <span className="hidden sm:inline">Clear</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Advanced Filters - Mobile Optimized */}
              {showFilters && (
                <div className={`pt-3 mt-3 border-t ${isDark ? 'border-blue-700' : 'border-gray-200'} sm:pt-4 sm:mt-4`}>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 sm:gap-4">
                    {/* Status Filter */}
                    <div>
                      <label className={`block mb-1 text-xs ${isDark ? 'text-blue-300' : 'text-gray-600'} sm:mb-2 sm:text-sm`}>Status</label>
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className={`w-full px-3 py-2 text-sm border rounded-lg sm:text-base ${textPrimary} ${bgInput} ${borderInput} focus:outline-none ${borderFocus} transition-colors duration-300`}
                      >
                        <option value="ALL">All Status</option>
                        <option value="PENDING">Pending</option>
                        <option value="APPROVED">Approved</option>
                        <option value="REJECTED">Rejected</option>
                      </select>
                    </div>

                    {/* Leave Type Filter */}
                    <div>
                      <label className={`block mb-1 text-xs ${isDark ? 'text-blue-300' : 'text-gray-600'} sm:mb-2 sm:text-sm`}>Leave Type</label>
                      <select
                        value={leaveTypeFilter}
                        onChange={(e) => setLeaveTypeFilter(e.target.value)}
                        className={`w-full px-3 py-2 text-sm border rounded-lg sm:text-base ${textPrimary} ${bgInput} ${borderInput} focus:outline-none ${borderFocus} transition-colors duration-300`}
                      >
                        <option value="ALL">All Types</option>
                        {uniqueLeaveTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>

                    {/* Date Range Filters */}
                    <div>
                      <label className={`block mb-1 text-xs ${isDark ? 'text-blue-300' : 'text-gray-600'} sm:mb-2 sm:text-sm`}>From Date</label>
                      <input
                        type="date"
                        value={dateRange.from}
                        onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
                        className={`w-full px-3 py-2 text-sm border rounded-lg sm:text-base ${textPrimary} ${bgInput} ${borderInput} focus:outline-none ${borderFocus} date-input transition-colors duration-300`}
                      />
                    </div>
                    <div>
                      <label className={`block mb-1 text-xs ${isDark ? 'text-blue-300' : 'text-gray-600'} sm:mb-2 sm:text-sm`}>To Date</label>
                      <input
                        type="date"
                        value={dateRange.to}
                        onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
                        className={`w-full px-3 py-2 text-sm border rounded-lg sm:text-base ${textPrimary} ${bgInput} ${borderInput} focus:outline-none ${borderFocus} date-input transition-colors duration-300`}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Results Count */}
              <div className={`mt-3 text-xs ${isDark ? 'text-blue-300' : 'text-gray-600'} sm:text-sm`}>
                Showing {filteredLeaves.length} of {leavesData?.length || 0} leave requests
              </div>
            </div>

            {/* Leaves List - Mobile Responsive Cards */}
            <div className="space-y-3 sm:space-y-4">
              {filteredLeaves.length === 0 ? (
                <div className={`p-6 text-center sm:p-8 ${bgCard} backdrop-blur-sm rounded-xl border ${borderColor}`}>
                  <p className={`text-sm ${isDark ? 'text-blue-300' : 'text-gray-600'} sm:text-base`}>No leave requests found</p>
                </div>
              ) : (
                filteredLeaves.map((leave) => (
                  <div
                    key={leave._id}
                    className={`p-4 transition-all duration-300 border ${isDark ? 'border-blue-700' : 'border-gray-200'} ${bgCard} backdrop-blur-sm rounded-xl ${bgCardHover}`}
                  >
                    {/* Mobile: Collapsible, Desktop: Always Expanded */}
                    <div className="block lg:hidden">
                      <button
                        onClick={() => setExpandedLeaveId(expandedLeaveId === leave._id ? null : leave._id)}
                        className="w-full text-left"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <h3 className={`text-base font-semibold ${textPrimary} sm:text-lg`}>
                                {leave.leaveType}
                              </h3>
                              {getStatusBadge(leave.status)}
                            </div>
                            <p className={`text-sm ${isDark ? 'text-blue-300' : 'text-gray-600'}`}>
                              {leave.userId?.user_name || "N/A"}
                            </p>
                            <p className={`mt-1 text-xs ${isDark ? 'text-blue-400' : 'text-gray-500'}`}>
                              {formatDate(leave.fromDate)} - {formatDate(leave.toDate)}
                            </p>
                          </div>
                          <div className={isDark ? 'text-blue-400' : 'text-gray-500'}>
                            {expandedLeaveId === leave._id ? <FaChevronUp /> : <FaChevronDown />}
                          </div>
                        </div>
                      </button>

                      {(expandedLeaveId === leave._id) && (
                        <div className={`pt-3 mt-3 border-t ${isDark ? 'border-blue-700' : 'border-gray-200'}`}>
                          <div className="space-y-2 text-sm">
                            <p className={isDark ? 'text-blue-300' : 'text-gray-600'}>
                              <span className={`font-medium ${textPrimary}`}>Department:</span>{" "}
                              {leave.userId?.user_department || "N/A"}
                            </p>
                            <p className={isDark ? 'text-blue-300' : 'text-gray-600'}>
                              <span className={`font-medium ${textPrimary}`}>Employee Code:</span>{" "}
                              {leave.userId?.user_roll || "N/A"}
                            </p>
                            <p className={isDark ? 'text-blue-300' : 'text-gray-600'}>
                              <span className={`font-medium ${textPrimary}`}>Duration:</span>{" "}
                              {formatDate(leave.fromDate)} - {formatDate(leave.toDate)}
                            </p>
                            <p className={isDark ? 'text-blue-300' : 'text-gray-600'}>
                              <span className={`font-medium ${textPrimary}`}>Days:</span>{" "}
                              {leave.days} day(s)
                            </p>
                            <p className={isDark ? 'text-blue-300' : 'text-gray-600'}>
                              <span className={`font-medium ${textPrimary}`}>Applied:</span>{" "}
                              {formatDate(leave.appliedAt)}
                            </p>
                            <p className={isDark ? 'text-blue-300' : 'text-gray-600'}>
                              <span className={`font-medium ${textPrimary}`}>Reason:</span>{" "}
                              {leave.reason}
                            </p>
                          </div>

                          <div className="flex flex-wrap gap-2 mt-4">
                            {leave.status === "PENDING" && (
                              <>
                                <button
                                  onClick={() => handleApproveLeave(leave._id)}
                                  className="flex items-center justify-center flex-1 gap-2 px-3 py-2 text-sm text-white transition-all duration-300 bg-green-700 rounded-lg sm:flex-none hover:bg-green-600"
                                >
                                  <FaCheck size={14} /> Approve
                                </button>
                                <button
                                  onClick={() => handleRejectLeave(leave._id)}
                                  className="flex items-center justify-center flex-1 gap-2 px-3 py-2 text-sm text-white transition-all duration-300 bg-red-700 rounded-lg sm:flex-none hover:bg-red-600"
                                >
                                  <FaTimes size={14} /> Reject
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => handleDeleteLeave(leave._id)}
                              className="flex items-center justify-center flex-1 gap-2 px-3 py-2 text-sm text-white transition-all duration-300 bg-gray-700 rounded-lg sm:flex-none hover:bg-gray-600"
                            >
                              <FaTrash size={14} /> Delete
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Desktop View */}
                    <div className="hidden lg:block">
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-3 mb-3">
                            <h3 className={`text-xl font-semibold ${textPrimary}`}>
                              {leave.leaveType}
                            </h3>
                            {getStatusBadge(leave.status)}
                          </div>
                          <div className={`grid grid-cols-2 gap-3 text-sm ${isDark ? 'text-blue-300' : 'text-gray-600'}`}>
                            <p>
                              <span className={`font-medium ${textPrimary}`}>Employee Name:</span>{" "}
                              {leave.userId?.user_name || "N/A"}
                            </p>
                            <p>
                              <span className={`font-medium ${textPrimary}`}>Department:</span>{" "}
                              {leave.userId?.user_department || "N/A"}
                            </p>
                            <p>
                              <span className={`font-medium ${textPrimary}`}>Employee Code:</span>{" "}
                              {leave.userId?.user_roll || "N/A"}
                            </p>
                            <p>
                              <span className={`font-medium ${textPrimary}`}>Duration:</span>{" "}
                              {formatDate(leave.fromDate)} - {formatDate(leave.toDate)}
                            </p>
                            <p>
                              <span className={`font-medium ${textPrimary}`}>Days:</span>{" "}
                              {leave.days} day(s)
                            </p>
                            <p>
                              <span className={`font-medium ${textPrimary}`}>Applied:</span>{" "}
                              {formatDate(leave.appliedAt)}
                            </p>
                          </div>
                          <p className={`mt-3 ${isDark ? 'text-blue-300' : 'text-gray-600'}`}>
                            <span className={`font-medium ${textPrimary}`}>Reason:</span>{" "}
                            {leave.reason}
                          </p>
                        </div>

                        <div className="flex gap-3">
                          {leave.status === "PENDING" && (
                            <>
                              <button
                                onClick={() => handleApproveLeave(leave._id)}
                                className="flex items-center gap-2 px-4 py-2 text-white transition-all duration-300 bg-green-700 rounded-lg hover:bg-green-600"
                                title="Approve Leave"
                              >
                                <FaCheck size={16} /> Approve
                              </button>
                              <button
                                onClick={() => handleRejectLeave(leave._id)}
                                className="flex items-center gap-2 px-4 py-2 text-white transition-all duration-300 bg-red-700 rounded-lg hover:bg-red-600"
                                title="Reject Leave"
                              >
                                <FaTimes size={16} /> Reject
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => handleDeleteLeave(leave._id)}
                            className="flex items-center gap-2 px-4 py-2 text-white transition-all duration-300 bg-gray-700 rounded-lg hover:bg-gray-600"
                            title="Delete Leave"
                          >
                            <FaTrash size={16} /> Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Holidays Section - Table View */}
        {activeTab === "holidays" && (
          <div>
            {/* Action Buttons */}
            <div className="flex flex-col gap-2 mb-4 sm:flex-row sm:mb-4">
              <button
                onClick={handleAddHoliday}
                className={`flex items-center justify-center gap-2 px-4 sm:px-5 py-2.5 ${bgButton} text-white rounded-lg transition-all duration-300 text-sm sm:text-base`}
              >
                <FaPlus size={14} className="sm:w-4 sm:h-4" /> Add Holiday
              </button>

              <button
                onClick={handleImportClick}
                disabled={importing}
                className="flex items-center justify-center gap-2 px-4 sm:px-5 py-2.5 bg-green-600 hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg transition-all duration-300 text-sm sm:text-base"
              >
                <FaFileExcel size={14} className="sm:w-4 sm:h-4" />
                {importing ? "Importing..." : "Import Excel"}
              </button>

              <button
                onClick={handleDownloadSample}
                className={`flex items-center justify-center gap-2 px-4 sm:px-5 py-2.5 ${bgCancel} ${textPrimary} rounded-lg transition-all duration-300 text-sm sm:text-base`}
              >
                <FaDownload size={14} className="sm:w-4 sm:h-4" /> Sample Template
              </button>

              {/* Hidden file input triggered by the Import Excel button */}
              <input
                type="file"
                ref={fileInputRef}
                accept=".xlsx,.xls,.csv"
                onChange={handleFileImport}
                className="hidden"
              />
            </div>

            {/* Helper text explaining the expected Excel format */}
            <p className={`mb-4 text-xs sm:text-sm ${isDark ? 'text-blue-300' : 'text-gray-600'}`}>
              Excel file must have exactly two columns:{" "}
              <span className={`font-medium ${textPrimary}`}>Date</span> and{" "}
              <span className={`font-medium ${textPrimary}`}>Holiday Name</span>.
              Download the sample template above for the correct format.
            </p>

            {/* Search + Filter Bar for Holidays */}
            <div className={`p-3 mb-4 sm:p-4 ${bgCard} backdrop-blur-sm rounded-xl border ${borderColor} transition-colors duration-300`}>
              <div className="flex flex-col flex-wrap gap-3 sm:flex-row sm:items-center sm:gap-4">
                {/* Search by Holiday Name */}
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <FaSearch className={`absolute text-sm transform -translate-y-1/2 left-3 top-1/2 ${isDark ? 'text-blue-400' : 'text-gray-500'} sm:text-base`} />
                    <input
                      type="text"
                      placeholder="Search by holiday name..."
                      value={holidaySearchTerm}
                      onChange={(e) => setHolidaySearchTerm(e.target.value)}
                      className={`w-full py-2 pr-3 text-sm border rounded-lg pl-9 sm:pl-10 sm:pr-4 sm:text-base ${textPrimary} ${bgInput} ${borderInput} focus:outline-none ${borderFocus} transition-colors duration-300`}
                    />
                  </div>
                </div>

                {/* Month Filter */}
                <div className="flex items-center gap-2 min-w-[160px]">
                  <FaCalendarAlt className={`flex-shrink-0 text-sm ${isDark ? 'text-blue-400' : 'text-gray-500'}`} />
                  <select
                    value={holidayMonthFilter}
                    onChange={(e) => setHolidayMonthFilter(e.target.value)}
                    className={`w-full px-3 py-2 text-sm border rounded-lg sm:text-base ${textPrimary} ${bgInput} ${borderInput} focus:outline-none ${borderFocus} transition-colors duration-300`}
                  >
                    <option value="ALL">All Months</option>
                    {MONTH_NAMES.map((m, idx) => (
                      <option key={m} value={idx}>{m}</option>
                    ))}
                  </select>
                </div>

                {/* Year Filter */}
                <div className="min-w-[120px]">
                  <select
                    value={holidayYearFilter}
                    onChange={(e) => setHolidayYearFilter(e.target.value)}
                    className={`w-full px-3 py-2 text-sm border rounded-lg sm:text-base ${textPrimary} ${bgInput} ${borderInput} focus:outline-none ${borderFocus} transition-colors duration-300`}
                  >
                    <option value="ALL">All Years</option>
                    {holidayYears.map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>

                {(holidaySearchTerm || holidayMonthFilter !== "ALL" || holidayYearFilter !== "ALL") && (
                  <button
                    onClick={clearHolidayFilters}
                    className="flex items-center justify-center gap-2 px-3 py-2 text-sm text-white transition-all duration-300 bg-red-700 rounded-lg sm:px-4 sm:text-base hover:bg-red-600"
                  >
                    <FaTimesCircle size={14} />
                    <span className="hidden sm:inline">Clear</span>
                  </button>
                )}
              </div>

              {/* Results Count */}
              <div className={`mt-3 text-xs ${isDark ? 'text-blue-300' : 'text-gray-600'} sm:text-sm`}>
                Showing {filteredHolidays.length} of {holidaysData?.length || 0} holidays
              </div>
            </div>

            {/* Holidays Table - scrollable body with sticky header */}
            <div className={`rounded-xl border ${borderColor} ${bgCard} backdrop-blur-sm transition-colors duration-300 overflow-hidden`}>
              <div className="max-h-[65vh] overflow-y-auto overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead className={`sticky top-0 z-10 ${bgTableHeader}`}>
                    <tr className={`border-b ${isDark ? "border-blue-700" : "border-gray-300"}`}>
                      <th className={`px-4 py-3 text-left font-semibold ${textPrimary} sm:text-base whitespace-nowrap`}>
                        Date
                      </th>
                      <th className={`px-4 py-3 text-left font-semibold ${textPrimary} sm:text-base`}>
                        Holiday Name
                      </th>
                      <th className={`px-4 py-3 text-right font-semibold ${textPrimary} sm:text-base whitespace-nowrap`}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredHolidays.length === 0 ? (
                      <tr>
                        <td
                          colSpan={3}
                          className={`px-4 py-8 text-center text-sm sm:text-base ${isDark ? "text-blue-300" : "text-gray-600"}`}
                        >
                          {holidaysData?.length === 0
                            ? "No holidays added yet"
                            : "No holidays match your search/filters"}
                        </td>
                      </tr>
                    ) : (
                      filteredHolidays.map((holiday) => (
                        <tr
                          key={holiday._id}
                          className={`border-b last:border-b-0 transition-colors duration-200 ${
                            isDark ? "border-blue-800/50 hover:bg-blue-800/20" : "border-gray-100 hover:bg-gray-50"
                          }`}
                        >
                          <td className={`px-4 py-3 text-sm sm:text-base whitespace-nowrap ${isDark ? "text-blue-300" : "text-gray-600"}`}>
                            {formatDate(holiday.DateString)}
                          </td>
                          <td className={`px-4 py-3 text-sm font-medium sm:text-base ${textPrimary}`}>
                            {holiday.Holidayname}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex justify-end gap-3">
                              <button
                                onClick={() => handleEditHoliday(holiday)}
                                className={`${isDark ? "text-blue-400 hover:text-blue-300" : "text-blue-600 hover:text-blue-800"} transition-colors`}
                                title="Edit Holiday"
                              >
                                <FaEdit size={14} className="sm:w-4 sm:h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteHoliday(holiday._id)}
                                className={`${isDark ? "text-red-400 hover:text-red-300" : "text-red-600 hover:text-red-800"} transition-colors`}
                                title="Delete Holiday"
                              >
                                <FaTrash size={14} className="sm:w-4 sm:h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Holiday Modal - Mobile Responsive */}
        {isHolidayModalOpen && (
          <div className={`fixed inset-0 z-50 flex items-center justify-center p-3 overflow-y-auto sm:p-4 ${isDark ? 'bg-black/60' : 'bg-gray-500/40'}`}>
            <div className={`w-full max-w-md p-4 mx-3 border ${isDark ? 'border-blue-700' : 'border-gray-300'} ${bgModal} rounded-xl sm:mx-4 transition-colors duration-300`}>
              <h2 className={`mb-3 text-xl font-bold ${textPrimary} sm:mb-4 sm:text-2xl`}>
                {editingHoliday ? "Edit Holiday" : "Add New Holiday"}
              </h2>
              <form onSubmit={handleSubmitHoliday}>
                <div className="mb-3 sm:mb-4">
                  <label className={`block mb-1 text-xs ${isDark ? 'text-blue-300' : 'text-gray-600'} sm:mb-2 sm:text-sm`}>Holiday Name</label>
                  <input
                    type="text"
                    required
                    value={holidayForm.Holidayname}
                    onChange={(e) =>
                      setHolidayForm({ ...holidayForm, Holidayname: e.target.value })
                    }
                    className={`w-full px-3 py-2 text-sm border rounded-lg sm:px-4 sm:text-base ${textPrimary} ${bgInput} ${borderInput} focus:outline-none ${borderFocus} transition-colors duration-300`}
                    placeholder="Enter holiday name"
                  />
                </div>
                <div className="mb-4 sm:mb-6">
                  <label className={`block mb-1 text-xs ${isDark ? 'text-blue-300' : 'text-gray-600'} sm:mb-2 sm:text-sm`}>Date</label>
                  <input
                    type="date"
                    required
                    value={holidayForm.DateString}
                    onChange={(e) =>
                      setHolidayForm({ ...holidayForm, DateString: e.target.value })
                    }
                    className={`w-full px-3 py-2 text-sm border rounded-lg sm:px-4 sm:text-base ${textPrimary} ${bgInput} ${borderInput} focus:outline-none ${borderFocus} date-input transition-colors duration-300`}
                  />
                </div>
                <div className="flex flex-col-reverse justify-end gap-2 sm:flex-row sm:gap-3">
                  <button
                    type="button"
                    onClick={() => setIsHolidayModalOpen(false)}
                    className={`px-4 py-2 text-sm transition-colors rounded-lg sm:text-base ${textPrimary} ${bgCancel}`}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`px-4 py-2 text-sm text-white transition-colors rounded-lg sm:text-base ${bgButton}`}
                  >
                    {editingHoliday ? "Update" : "Add"} Holiday
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ─── Generic Confirmation Popup (replaces window.confirm everywhere) ─── */}
        {confirmModal.isOpen && (
          <div className={`fixed inset-0 z-[60] flex items-center justify-center p-3 sm:p-4 ${isDark ? 'bg-black/60' : 'bg-gray-500/40'}`}>
            <div className={`w-full max-w-sm p-5 mx-3 border ${isDark ? 'border-blue-700' : 'border-gray-300'} ${bgModal} rounded-xl sm:mx-4 transition-colors duration-300`}>
              <div className="flex items-start gap-3 mb-4">
                <div className={`flex items-center justify-center w-9 h-9 rounded-full flex-shrink-0 ${
                  confirmModal.confirmColor.includes("red")
                    ? (isDark ? "bg-red-900/50 text-red-400" : "bg-red-100 text-red-600")
                    : (isDark ? "bg-green-900/50 text-green-400" : "bg-green-100 text-green-600")
                }`}>
                  <FaExclamationTriangle size={16} />
                </div>
                <div>
                  <h3 className={`text-base font-bold sm:text-lg ${textPrimary}`}>
                    {confirmModal.title}
                  </h3>
                  <p className={`mt-1 text-sm ${isDark ? 'text-blue-300' : 'text-gray-600'}`}>
                    {confirmModal.message}
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={closeConfirm}
                  disabled={confirmProcessing}
                  className={`px-4 py-2 text-sm transition-colors rounded-lg disabled:opacity-60 disabled:cursor-not-allowed ${textPrimary} ${bgCancel}`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmAction}
                  disabled={confirmProcessing}
                  className={`px-4 py-2 text-sm text-white transition-colors rounded-lg disabled:opacity-70 disabled:cursor-not-allowed ${confirmModal.confirmColor}`}
                >
                  {confirmProcessing ? "Please wait..." : confirmModal.confirmText}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaveManagement;
